/**
 * Best-effort split of pasted text into a playbook's description and checks, the
 * same way mc-api converted the old Markdown playbooks: each top-level list
 * item is a check, lines under it are its notes, and prose before the first
 * item is the description. Text without a list becomes one check per line.
 * Front matter and headings are dropped.
 */
export interface ParsedPlaybook {
  description?: string;
  checks: { text: string; notes?: string }[];
}

const LIST_ITEM = /^(?:[-*+•]|\d+[.)])\s+(.*)$/;
/** `**login** — Log in` (the old Markdown format) keeps only the text after the bold id. */
const BOLD_ID = /^\*\*([^*]+)\*\*\s*(?:[—–:-]+\s*(.*))?$/;
const FRONT_MATTER = /^---\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/;

/** Joins hard-wrapped lines into paragraphs separated by a blank line. */
const unwrap = (lines: string[]) =>
  lines
    .join("\n")
    .split(/\n\s*\n/)
    .map((p) =>
      p
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .join(" "),
    )
    .filter(Boolean)
    .join("\n\n");

export function playbookFromText(text: string): ParsedPlaybook {
  const lines = text
    .replace(FRONT_MATTER, "")
    .split(/\r?\n/)
    .filter((l) => !/^#{1,6}\s/.test(l));
  if (!lines.some((l) => LIST_ITEM.test(l))) {
    return {
      checks: lines
        .map((l) => l.trim())
        .filter(Boolean)
        .map((t) => ({ text: t })),
    };
  }
  const description: string[] = [];
  const checks: { text: string; notes: string[] }[] = [];
  for (const line of lines) {
    const item = LIST_ITEM.exec(line);
    const current = checks[checks.length - 1];
    if (item) {
      const head = item[1].trim();
      const bold = BOLD_ID.exec(head);
      checks.push({
        text: (bold ? bold[2] || bold[1] : head).trim(),
        notes: [],
      });
    } else if (current) {
      if (line.trim()) current.notes.push(line.trim());
    } else description.push(line);
  }
  return {
    description: unwrap(description) || undefined,
    checks: checks
      .filter((c) => c.text)
      .map((c) => ({
        text: c.text,
        ...(c.notes.length > 0 && { notes: c.notes.join("\n") }),
      })),
  };
}
